import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { isNativeShell } from './platform.js';

/**
 * Lembretes com hora marcada no Android: o app entrega a lista e o aparelho
 * agenda cada um no relógio do sistema (alarme exato), mostra o aviso na hora
 * e refaz tudo depois de reinício. Fora do Android instalado (navegador, iOS),
 * nada aqui faz efeito e os lembretes seguem pelo caminho de sempre.
 */

export type AlarmMode = 'notice' | 'alarm';

export type AlarmItem = {
  /** Identidade estável do lembrete: a mesma a cada sincronização. */
  id: string;
  /** Hora do aviso, em milissegundos desde 1970. */
  at: number;
  title: string;
  body: string;
  /** Tela que abre no toque (começa com "/"). */
  route?: string;
  /** "alarm" toca no volume de alarme, como despertador; o padrão é o aviso comum. */
  mode?: AlarmMode;
  /** Ler o lembrete em voz alta depois do aviso. */
  speak?: boolean;
};

export type AlarmStatus = {
  /** Os avisos do app estão liberados. */
  notifications: boolean;
  /** O aparelho deixa agendar no minuto exato; sem isso, a hora não é garantida. */
  exactAlarm: boolean;
  /** A economia de bateria pode segurar o app em segundo plano. */
  batteryOptimized: boolean;
  manufacturer: string;
  sdk: number;
};

export type AlarmSettingsScreen = 'exactAlarm' | 'notifications' | 'battery';
export type AlarmAction = { id: string; route: string };
export type AlarmFired = Required<AlarmItem>;
export type AlarmTestOptions = {
  title?: string;
  body?: string;
  route?: string;
  mode?: AlarmMode;
  speak?: boolean;
};

type AlarmPlugin = {
  status(): Promise<AlarmStatus>;
  sync(options: { items: AlarmItem[] }): Promise<{ scheduled: number; exact: boolean }>;
  cancelAll(): Promise<void>;
  test(
    options: { inSeconds: number } & AlarmTestOptions,
  ): Promise<{ id: string; at: number; exact: boolean }>;
  openSettings(options: { which: AlarmSettingsScreen }): Promise<void>;
  addListener(
    event: 'alarmAction',
    listener: (data: AlarmAction) => void,
  ): Promise<PluginListenerHandle>;
  addListener(
    event: 'alarmFired',
    listener: (data: AlarmFired) => void,
  ): Promise<PluginListenerHandle>;
};

const plugin = registerPlugin<AlarmPlugin>('SecretarioAlarm');

/** Só no app Android que já traz a parte nativa dos lembretes. */
export function alarmsAvailable(): boolean {
  try {
    return (
      isNativeShell() &&
      Capacitor.getPlatform() === 'android' &&
      Capacitor.isPluginAvailable('SecretarioAlarm')
    );
  } catch {
    return false;
  }
}

/** Chama a parte nativa sem deixar erro dela derrubar a tela. */
async function quiet<T>(call: () => Promise<T>): Promise<T | null> {
  if (!alarmsAvailable()) return null;
  try {
    return await call();
  } catch {
    return null;
  }
}

/** O que falta para o lembrete chegar na hora. Devolve null fora do Android instalado. */
export function alarmStatus(): Promise<AlarmStatus | null> {
  return quiet(() => plugin.status());
}

/**
 * Deixa agendado exatamente o que está na lista (o que saiu é cancelado).
 * Devolve null quando o aparelho não assumiu os lembretes; `exact` diz se a
 * hora ficou garantida no minuto.
 */
export function syncAlarms(
  items: AlarmItem[],
): Promise<{ scheduled: number; exact: boolean } | null> {
  return quiet(() => plugin.sync({ items }));
}

/** Agenda um lembrete de teste para daqui a alguns segundos. */
export async function testAlarm(seconds: number, options: AlarmTestOptions = {}): Promise<boolean> {
  return (await quiet(() => plugin.test({ inSeconds: seconds, ...options }))) !== null;
}

/** Abre a tela do sistema em que a pessoa libera o que falta. */
export async function openAlarmSettings(which: AlarmSettingsScreen): Promise<boolean> {
  if (!alarmsAvailable()) return false;
  try {
    await plugin.openSettings({ which });
    return true;
  } catch {
    return false;
  }
}

/** Nada mais fica agendado neste aparelho (saída da conta). */
export async function cancelAllAlarms(): Promise<void> {
  await quiet(() => plugin.cancelAll());
}

async function listen(register: () => Promise<PluginListenerHandle>): Promise<() => void> {
  if (!alarmsAvailable()) return () => {};
  try {
    const handle = await register();
    return () => void handle.remove();
  } catch {
    return () => {};
  }
}

/** Toque no aviso de um lembrete: recebe a tela a abrir. Devolve como desligar. */
export function onAlarmAction(handler: (action: AlarmAction) => void): Promise<() => void> {
  return listen(() => plugin.addListener('alarmAction', handler));
}

/** Lembrete que disparou com o app aberto. Devolve como desligar. */
export function onAlarmFired(handler: (fired: AlarmFired) => void): Promise<() => void> {
  return listen(() => plugin.addListener('alarmFired', handler));
}
